import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Phone, Mail, Clock, Search, User, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
const LOGO_URL = 'https://drwzpk38qkpfb.cloudfront.net/site12118-1.websiteforge.com/preview/uploaded/images/Design/logo.svg';
const TopBar = () => <div className="bg-ink text-gray-200 text-sm py-2.5 px-4 border-b border-white/5" data-id="element-74">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2" data-id="element-75">
      <div className="flex items-center gap-5 font-medium" data-id="element-76">
        <span className="hidden md:inline" data-id="element-77">
          Wholesale custom uniforms and team apparel
        </span>
        <span className="flex items-center gap-1.5" data-id="element-78">
          <Clock className="w-4 h-4 text-brandRed" data-id="element-79" /> Support 9am–10pm EST
        </span>
      </div>
      <div className="flex items-center gap-5 font-medium" data-id="element-80">
        <a href="tel:8886998511" className="flex items-center gap-1.5 hover:text-white transition-colors" data-id="element-81">
          <Phone className="w-4 h-4" data-id="element-82" /> (888) 699-8511
        </a>
        <a href="mailto:info@customuniformsupplier.com" className="hidden md:flex items-center gap-1.5 hover:text-white transition-colors" data-id="element-83">
          <Mail className="w-4 h-4" data-id="element-84" /> info@customuniformsupplier.com
        </a>
      </div>
    </div>
  </div>;
// Mega menu data
const featuredSeasonal = ['football', 'soccer', 'baseball', 'softball', 'basketball', 'volleyball'];
const teamSports = ['baseball', 'football', 'basketball', 'softball', 'soccer', 'volleyball', 'hockey', 'lacrosse'];
const specialty = ['track-and-field', 'wrestling', 'cheer', 'fishing', 'groupwear', 'accessories'];
const getCat = (slug: string) => categories.find(c => c.slug === slug);
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedMobile(null);
    setActiveMenu(null);
  }, [location.pathname]);
  const mainNav = [{
    label: 'Home',
    path: '/'
  }, {
    label: 'Products',
    path: '/products',
    hasMega: true
  }, {
    label: 'Kit Builder',
    path: '/kit-builder'
  }, {
    label: 'For Dealers',
    path: '/wholesale-apply'
  }, {
    label: 'Find a Supplier',
    path: '/find-a-supplier'
  }, {
    label: 'Why Tier One',
    path: '/why-tier-one'
  }, {
    label: 'How It Works',
    path: '/how-it-works'
  }, {
    label: 'Production & Support',
    path: '/production-support'
  }, {
    label: 'Contact',
    path: '/contact'
  }];
  return <header className="sticky top-0 z-50 bg-white border-b border-gray200 shadow-sm" data-id="element-85">
      {/* Main header row */}
      <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between gap-6" data-id="element-86">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-id="element-87">
          <img src={LOGO_URL} alt="Custom Uniform Supplier" className="h-11 w-auto" data-id="element-88" />
          <div className="hidden lg:block border-l border-gray200 pl-3 ml-1" data-id="element-89">
            <div className="text-xs font-bold tracking-wide text-charcoal uppercase" data-id="element-90">
              Tier 1 Apparel
            </div>
            <div className="text-xs text-gray-500" data-id="element-91">
              Built To Elevate Your Brand
            </div>
          </div>
        </Link>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-2 lg:gap-3 shrink-0" data-id="element-92">
          <button className="p-2 text-gray-500 hover:text-charcoal transition-colors" aria-label="Search" data-id="element-93">
            <Search className="w-5 h-5" data-id="element-94" />
          </button>
          <button className="p-2 text-gray-500 hover:text-charcoal transition-colors flex items-center gap-2 text-sm font-semibold" data-id="element-95">
            <User className="w-5 h-5" data-id="element-96" />
            <span className="hidden xl:inline" data-id="element-97">Sign In</span>
          </button>
          <Link to="/wholesale-apply" className="hidden xl:inline-block text-sm font-bold text-charcoal hover:text-brandRed transition-colors px-3 py-2 border border-gray200 rounded-sm" data-id="element-98">
            Apply for Wholesale
          </Link>
          <Link to="/kit-builder" className="bg-brandRed hover:bg-brandRedDark text-white text-sm font-bold py-2.5 px-5 rounded-sm transition-colors shadow-sm" data-id="element-99">
            Start Your Design
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button className="lg:hidden p-2 text-charcoal" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-100">
          {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-101" /> : <Menu className="w-6 h-6" data-id="element-102" />}
        </button>
      </div>

      {/* Nav row */}
      <nav className="hidden lg:block border-t border-gray100 bg-white" onMouseLeave={() => setActiveMenu(null)} data-id="element-103">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-12" data-id="element-104">
          {mainNav.map(item => {
          const isActive = location.pathname === item.path || item.hasMega && activeMenu === 'products';
          return <div key={item.label} className="relative h-full" data-id="element-105">
                {item.hasMega ? <button onMouseEnter={() => setActiveMenu('products')} className={cn('h-full px-4 text-sm font-bold uppercase tracking-wide flex items-center gap-1.5 transition-colors border-b-2', isActive ? 'text-brandRed border-brandRed' : 'text-charcoal border-transparent hover:text-brandRed')} data-id="element-106">
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" data-id="element-107" />
                  </button> : <Link to={item.path} onMouseEnter={() => setActiveMenu(null)} className={cn('h-full px-4 text-sm font-bold uppercase tracking-wide flex items-center transition-colors border-b-2', isActive ? 'text-brandRed border-brandRed' : 'text-charcoal border-transparent hover:text-brandRed')} data-id="element-108">
                    {item.label}
                  </Link>}
              </div>;
        })}
        </div>

        {/* Products Mega Menu */}
        <AnimatePresence data-id="element-109">
          {activeMenu === 'products' && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2
        }} className="absolute left-0 right-0 top-full bg-white border-t border-gray200 shadow-2xl" data-id="element-110">
              <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-4 gap-8" data-id="element-111">
                <MegaColumn title="Featured / Seasonal" slugs={featuredSeasonal} accent data-id="element-112" />
                <MegaColumn title="Team Sports" slugs={teamSports} data-id="element-113" />
                <MegaColumn title="Specialty / Apparel" slugs={specialty} data-id="element-114" />
                <div data-id="element-115">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4" data-id="element-116">
                    Wholesale Tools
                  </h4>
                  <ul className="flex flex-col gap-3" data-id="element-117">
                    <li data-id="element-118">
                      <Link to="/kit-builder" className="text-sm font-semibold text-charcoal hover:text-brandRed transition-colors flex items-center gap-2" data-id="element-119">
                        Start Your Design{' '}
                        <ChevronRight className="w-3.5 h-3.5" data-id="element-120" />
                      </Link>
                    </li>
                    <li data-id="element-121">
                      <Link to="/wholesale-apply" className="text-sm font-semibold text-charcoal hover:text-brandRed transition-colors flex items-center gap-2" data-id="element-122">
                        Apply for Wholesale Access{' '}
                        <ChevronRight className="w-3.5 h-3.5" data-id="element-123" />
                      </Link>
                    </li>
                    <li data-id="element-124">
                      <Link to="/find-a-supplier" className="text-sm font-semibold text-charcoal hover:text-brandRed transition-colors flex items-center gap-2" data-id="element-125">
                        Find a Supplier <ChevronRight className="w-3.5 h-3.5" data-id="element-126" />
                      </Link>
                    </li>
                    <li data-id="element-127">
                      <Link to="/samples/size" className="text-sm text-gray-600 hover:text-brandRed transition-colors" data-id="element-128">
                        Size Samples
                      </Link>
                    </li>
                    <li data-id="element-129">
                      <Link to="/samples/product" className="text-sm text-gray-600 hover:text-brandRed transition-colors" data-id="element-130">
                        Product Samples
                      </Link>
                    </li>
                  </ul>

                  <div className="mt-6 p-4 bg-ink rounded-lg" data-id="element-131">
                    <div className="text-xs font-bold text-brandRed uppercase tracking-wider mb-1.5" data-id="element-132">
                      Dealer Access
                    </div>
                    <p className="text-sm text-gray-300 mb-3 leading-snug" data-id="element-133">
                      Wholesale pricing requires an approved dealer account.
                    </p>
                    <Link to="/wholesale-apply" className="text-xs font-bold text-white hover:text-brandRed transition-colors flex items-center gap-1" data-id="element-134">
                      Apply Now <ChevronRight className="w-3 h-3" data-id="element-135" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-136">
        {isMobileMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="lg:hidden bg-white border-t border-gray200 overflow-hidden" data-id="element-137">
            <div className="px-4 py-6 flex flex-col gap-4 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-138">
              <div className="flex flex-col gap-2.5" data-id="element-139">
                <Link to="/kit-builder" className="bg-brandRed text-white text-center font-bold py-3.5 rounded-sm text-base" data-id="element-140">
                  Start Your Design
                </Link>
                <Link to="/wholesale-apply" className="border-2 border-charcoal text-charcoal text-center font-bold py-3 rounded-sm text-base" data-id="element-141">
                  Apply for Wholesale Access
                </Link>
              </div>

              <ul className="flex flex-col mt-3 border-y border-gray100 divide-y divide-gray100" data-id="element-142">
                {mainNav.map(item => <li key={item.label} data-id="element-143">
                    {item.hasMega ? <>
                        <button className="w-full flex items-center justify-between py-3.5 text-left font-bold text-charcoal text-base" onClick={() => setExpandedMobile(expandedMobile === 'products' ? null : 'products')} data-id="element-144">
                          {item.label}
                          <ChevronDown className={cn('w-5 h-5 transition-transform', expandedMobile === 'products' && 'rotate-180')} data-id="element-145" />
                        </button>
                        <AnimatePresence data-id="element-146">
                          {expandedMobile === 'products' && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden" data-id="element-147">
                              <div className="pb-3 flex flex-col gap-1.5" data-id="element-148">
                                {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="text-sm text-gray-700 py-2 pl-4" data-id="element-149">
                                    {cat.name}
                                  </Link>)}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={item.path} className="py-3.5 font-bold text-charcoal text-base flex items-center justify-between" data-id="element-150">
                        {item.label}
                        <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-151" />
                      </Link>}
                  </li>)}
              </ul>

              <button className="text-base font-semibold text-charcoal py-2 flex items-center justify-between mt-2" data-id="element-152">
                <span className="flex items-center gap-2" data-id="element-153">
                  <User className="w-5 h-5" data-id="element-154" /> Sign In / Account
                </span>
                <ChevronRight className="w-5 h-5" data-id="element-155" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
};
const MegaColumn = ({
  title,
  slugs,
  accent = false
}: {
  title: string;
  slugs: string[];
  accent?: boolean;
}) => <div data-id="element-156">
    <h4 className={cn('text-xs font-bold uppercase tracking-wider mb-4', accent ? 'text-brandRed' : 'text-gray-400')} data-id="element-157">
      {title}
    </h4>
    <ul className="flex flex-col gap-2.5" data-id="element-158">
      {slugs.map(slug => {
      const cat = getCat(slug);
      if (!cat) return null;
      return <li key={slug} data-id="element-159">
            <Link to={`/category/${slug}`} className="text-sm font-semibold text-charcoal hover:text-brandRed transition-colors group flex items-center justify-between" data-id="element-160">
              <span data-id="element-161">{cat.name}</span>
              <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-brandRed transition-colors" data-id="element-162" />
            </Link>
          </li>;
    })}
    </ul>
  </div>;
const Footer = () => {
  const footerCols = [{
    title: 'Company',
    links: [{
      label: 'Home',
      to: '/'
    }, {
      label: 'Why Tier One',
      to: '/why-tier-one'
    }, {
      label: 'How It Works',
      to: '/how-it-works'
    }, {
      label: 'Contact',
      to: '/contact'
    }, {
      label: 'Production & Support',
      to: '/production-support'
    }]
  }, {
    title: 'Wholesale',
    links: [{
      label: 'Apply for Wholesale Access',
      to: '/wholesale-apply'
    }, {
      label: 'Dealer Support',
      to: '/dealer-support'
    }, {
      label: 'Private-Label Production',
      to: '/private-label'
    }, {
      label: 'Combined Quantity Pricing',
      to: '/combined-pricing'
    }, {
      label: 'Account Sign In',
      to: '/login'
    }]
  }, {
    title: 'Teams & Organizations',
    links: [{
      label: 'Find a Supplier',
      to: '/find-a-supplier'
    }, {
      label: 'Submit Team Request',
      to: '/find-a-supplier'
    }, {
      label: 'Schools & Leagues',
      to: '/find-a-supplier'
    }, {
      label: 'Team Gear Support',
      to: '/find-a-supplier'
    }]
  }, {
    title: 'Tools',
    links: [{
      label: 'Kit Builder',
      to: '/kit-builder'
    }, {
      label: 'Start Your Design',
      to: '/kit-builder'
    }, {
      label: 'Size Samples',
      to: '/samples/size'
    }, {
      label: 'Product Samples',
      to: '/samples/product'
    }, {
      label: 'Order Support',
      to: '/order-support'
    }]
  }];
  return <footer className="bg-ink text-white pt-20 pb-8" data-id="element-163">
      <div className="max-w-7xl mx-auto px-4" data-id="element-164">
        {/* Top: Brand + CTAs */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 mb-16 pb-16 border-b border-white/10" data-id="element-165">
          <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-166">
            <img src={LOGO_URL} alt="Custom Uniform Supplier" className="h-14 w-auto brightness-0 invert opacity-95 object-contain object-left" data-id="element-167" />
            <p className="text-gray-300 text-base leading-relaxed max-w-md" data-id="element-168">
              Wholesale custom uniforms, private-label apparel, and team gear
              built for dealers, resellers, team stores, decorators, sporting
              goods stores, apparel companies, and larger buyers.
            </p>
            <div className="flex flex-col gap-2.5 text-base text-gray-200" data-id="element-169">
              <a href="tel:8886998511" className="flex items-center gap-2.5 hover:text-white transition-colors" data-id="element-170">
                <Phone className="w-5 h-5 text-brandRed" data-id="element-171" /> (888) 699-8511
              </a>
              <a href="mailto:info@customuniformsupplier.com" className="flex items-center gap-2.5 hover:text-white transition-colors" data-id="element-172">
                <Mail className="w-5 h-5 text-brandRed" data-id="element-173" />{' '}
                info@customuniformsupplier.com
              </a>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-2" data-id="element-174">
              <Link to="/kit-builder" className="bg-brandRed hover:bg-brandRedDark text-white text-base font-bold py-3 px-5 rounded-sm text-center transition-colors" data-id="element-175">
                Start Your Design
              </Link>
              <Link to="/wholesale-apply" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-base font-bold py-3 px-5 rounded-sm text-center transition-colors" data-id="element-176">
                Apply for Wholesale Access
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-177">
            {footerCols.map(col => <div key={col.title} data-id="element-178">
                <h4 className="font-heading font-bold text-white mb-5 uppercase tracking-wider text-sm" data-id="element-179">
                  {col.title}
                </h4>
                <ul className="flex flex-col gap-3 text-sm text-gray-400" data-id="element-180">
                  {col.links.map(link => <li key={link.label} data-id="element-181">
                      <Link to={link.to} className="hover:text-white transition-colors leading-snug" data-id="element-182">
                        {link.label}
                      </Link>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>

        {/* Categories row */}
        <div className="mb-12 pb-12 border-b border-white/10" data-id="element-183">
          <h4 className="font-heading font-bold text-white mb-5 uppercase tracking-wider text-sm" data-id="element-184">
            Categories
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm" data-id="element-185">
            {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="text-gray-300 hover:text-white transition-colors font-medium" data-id="element-186">
                {cat.name}
              </Link>)}
          </div>
        </div>

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-187">
          <div className="text-gray-400 text-sm" data-id="element-188">
            © 2026 Custom Uniform Supplier / Tier One Apparel
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400" data-id="element-189">
            <Link to="/terms" className="hover:text-white transition-colors" data-id="element-190">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-id="element-191">
              Privacy Policy
            </Link>
            <Link to="/returns" className="hover:text-white transition-colors" data-id="element-192">
              Returns Policy
            </Link>
            <Link to="/design-policy" className="hover:text-white transition-colors" data-id="element-193">
              Design Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>;
};
export const Layout = () => <div className="min-h-screen flex flex-col font-sans text-charcoal" data-id="element-194">
    <TopBar data-id="element-195" />
    <Header data-id="element-196" />
    <main className="flex-grow" data-id="element-197">
      <Outlet data-id="element-198" />
    </main>
    <Footer data-id="element-199" />
  </div>;